import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import SalonHeaderActions from '../components/SalonHeaderActions';
import ExpectedTodayBookings from '../components/ExpectedTodayBookings';
import { logoutFranchiseFlow } from '../../shared/franchiseFlowAuth';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import {
  fetchOpenVisits,
  fetchCompletedVisits,
  fetchCheckinRecommendedProducts,
  setCheckinProductPreview,
  saveCheckinProductCart,
  placeCheckinProductOrder,
} from '../../salon-admin/utils/checkinQueueApi';
import RecommendedProductsPreviewModal from '../../admin-shared/RecommendedProductsPreviewModal';
import {
  fetchCheckinReportSessions,
  fetchReelsDeskIndex,
  downloadLobbyReel,
  reopenCheckinQueue,
} from '../../salon-admin/utils/checkinQueueApi';
import {
  SALON_STYLIST_CHECKIN_PATH,
  SALON_STYLIST_GUEST_PATH,
  SALON_SERVICE_WORK_PATH,
  SALON_SERVICE_CATALOG_PATH,
} from '../config/salonApp';
import {
  lobbyActionForVisit,
  lobbyShowAnalysisPreview,
  visitCanContinueFromLobby,
} from '../utils/lobbyVisitActions';
import { pendingAnalysisLines } from '../utils/analysisServiceSync';
import { deskServiceShortLabel } from '../../admin-shared/checkinServiceDesk';
import {
  clearServiceWorkPins,
  listServiceWorkPins,
  pinsForCheckin,
  syncServiceWorkPinsWithServerVisits,
} from '../utils/serviceWorkStorage';
import { filterCheckinsForToday } from '../utils/lobbyCheckinDay';
import { resolveExperienceHubPath, resolveSalonHomePath } from '../utils/salonHomeNav';
import { KIOSK_MODE_DESK, setSalonKioskMode } from '../utils/salonKioskMode';
import {
  hasNewGuestSinceLastSeen,
  markDeskGuestsSeen,
  playDeskChime,
} from '../utils/lobbyDeskNotify';
import {
  isSalonReadyForStylist,
  readVisitIntentOrNull,
  visitIntentLabel,
} from '../utils/visitIntent';
import { partitionVisitServices } from '../../admin-shared/checkinServiceDesk';
import {
  hasAnalysisProductsReady,
  lobbyPreviewCtaLabel,
  snapshotReportSessions,
} from '../utils/lobbyReportSessions';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './FlowSelectPage.css';
import './ServiceLobbyPage.css';
import './ServiceSessionPage.css';
import './VisitIntentPage.css';

const BackIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

function isVisitFinished(checkin) {
  const status = String(checkin?.status || '').toLowerCase();
  if (status === 'completed') return true;
  if (status === 'cancelled') return false;
  const services = checkin?.selected_services;
  if (!Array.isArray(services) || !services.length) return false;
  const { inProgress, pending, completed } = partitionVisitServices(services);
  return completed.length > 0 && inProgress.length === 0 && pending.length === 0;
}

function pickLobbyReel(reelsByCheckin, checkinId) {
  const list = reelsByCheckin[String(checkinId)] || [];
  if (!list.length) return null;
  return list.find((r) => r.has_video) || list[0];
}

function formatFinishedTime(checkin) {
  const raw = checkin?.completed_at || checkin?.updated_at;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function ServiceLobbyPage() {
  const navigate = useNavigate();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  useFaceIdentityScrollShell();
  const homePath = resolveExperienceHubPath(resolveSalonHomePath());

  const [openVisits, setOpenVisits] = useState([]);
  const [finishedVisits, setFinishedVisits] = useState([]);
  const [pins, setPins] = useState(() => listServiceWorkPins());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newGuestAlert, setNewGuestAlert] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [previewCheckin, setPreviewCheckin] = useState(null);
  const [previewProducts, setPreviewProducts] = useState(null);
  const [previewTreatments, setPreviewTreatments] = useState([]);
  const [previewCart, setPreviewCart] = useState([]);
  const [previewPurchases, setPreviewPurchases] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewNotice, setPreviewNotice] = useState('');
  const [previewSkinSessionId, setPreviewSkinSessionId] = useState('');
  const [previewHairSessionId, setPreviewHairSessionId] = useState('');
  const [reelsByCheckin, setReelsByCheckin] = useState({});
  const [reelBusyId, setReelBusyId] = useState('');
  const deskNotifyReadyRef = useRef(false);

  useEffect(() => {
    setSalonKioskMode(KIOSK_MODE_DESK);
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const [rows, done, reelMap] = await Promise.all([
        fetchOpenVisits(),
        fetchCompletedVisits(),
        fetchReelsDeskIndex().catch(() => ({})),
      ]);
      setReelsByCheckin(reelMap && typeof reelMap === 'object' ? reelMap : {});
      const todayOpen = filterCheckinsForToday(rows);
      const todayDone = filterCheckinsForToday(Array.isArray(done) ? done : []);
      syncServiceWorkPinsWithServerVisits(todayOpen, todayDone);
      if (deskNotifyReadyRef.current && hasNewGuestSinceLastSeen(todayOpen)) {
        setNewGuestAlert(true);
        playDeskChime();
      }
      markDeskGuestsSeen(todayOpen);
      deskNotifyReadyRef.current = true;
      setOpenVisits(todayOpen);
      setFinishedVisits(todayDone);
      setPins(listServiceWorkPins());
    } catch (err) {
      setError(err?.message || 'Could not load desk');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onQueue = () => refresh({ silent: true });
    window.addEventListener('checkin-queue-updated', onQueue);
    const timer = window.setInterval(() => refresh({ silent: true }), 12000);
    return () => {
      window.removeEventListener('checkin-queue-updated', onQueue);
      window.clearInterval(timer);
    };
  }, [refresh]);

  const finishedRows = useMemo(() => {
    const byId = new Map();
    for (const checkin of finishedVisits) {
      if (checkin?.id != null) byId.set(String(checkin.id), checkin);
    }
    // Only service start→end (or status completed). Analysis-only stays in Active.
    for (const checkin of openVisits) {
      if (!isVisitFinished(checkin) || checkin?.id == null) continue;
      if (!byId.has(String(checkin.id))) byId.set(String(checkin.id), checkin);
    }
    return [...byId.values()].sort((a, b) =>
      String(b.completed_at || b.updated_at || b.id || '').localeCompare(
        String(a.completed_at || a.updated_at || a.id || ''),
      ),
    );
  }, [finishedVisits, openVisits]);

  const finishedIds = useMemo(
    () => new Set(finishedRows.map((c) => String(c.id))),
    [finishedRows],
  );

  const rows = useMemo(() => {
    const activeOpen = openVisits.filter((c) => !finishedIds.has(String(c.id)));
    const merged = activeOpen.map((checkin) => ({
      checkin,
      visitPins: pinsForCheckin(checkin.id, pins),
    }));
    return merged.sort((a, b) => {
      const ac = isSalonReadyForStylist(a.checkin) ? 0 : 1;
      const bc = isSalonReadyForStylist(b.checkin) ? 0 : 1;
      if (ac !== bc) return ac - bc;
      return String(b.checkin?.id || '').localeCompare(String(a.checkin?.id || ''));
    });
  }, [openVisits, pins, finishedIds]);

  const handleReelDownload = useCallback(async (reel) => {
    if (!reel?.id || reelBusyId) return;
    setReelBusyId(reel.id);
    setError('');
    try {
      await downloadLobbyReel(reel.id);
    } catch (err) {
      setError(err?.message || 'Reel download failed');
    } finally {
      setReelBusyId('');
    }
  }, [reelBusyId]);

  const openGuestCheckInScreen = useCallback(() => {
    navigate(SALON_STYLIST_CHECKIN_PATH, { replace: false, state: {} });
  }, [navigate]);

  const handleBack = useCallback(() => {
    openGuestCheckInScreen();
  }, [openGuestCheckInScreen]);

  const handleHome = useCallback(() => {
    navigate(homePath, { replace: true, state: {} });
    refresh();
  }, [navigate, homePath, refresh]);

  const handleLogout = async () => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    try {
      clearServiceWorkPins();
      await logoutFranchiseFlow();
    } finally {
      navigate('/naturals/salon/login', { replace: true, state: {} });
      setLogoutBusy(false);
    }
  };

  const openGuest = (checkin, pin, mode) => {
    if (mode === 'disabled' || !checkin?.id) return;
    if (mode === 'work') {
      navigate(SALON_SERVICE_WORK_PATH, {
        state: {
          checkinId: checkin.id,
          checkinCode: checkin.customer_code,
          customerName: checkin.customer_name,
          stylistId: pin?.stylistId || '',
          stylistName: pin?.stylistName || '',
        },
      });
      return;
    }
    navigate(SALON_STYLIST_GUEST_PATH, {
      state: { checkinId: checkin.id },
    });
  };

  const continueGuestVisit = useCallback(
    async (checkin) => {
      if (!checkin?.id || !visitCanContinueFromLobby(checkin)) return;
      const status = String(checkin.status || '').toLowerCase();
      if (status === 'completed') {
        try {
          await reopenCheckinQueue(checkin.id);
          await refresh({ silent: true });
        } catch (err) {
          setError(err?.message || 'Could not reopen visit');
          return;
        }
      }
      navigate(SALON_STYLIST_GUEST_PATH, { state: { checkinId: checkin.id } });
    },
    [navigate, refresh],
  );

  const openFinishedPreview = useCallback(async (checkin) => {
    if (!checkin?.id) return;
    setPreviewCheckin(checkin);
    setPreviewProducts(null);
    setPreviewTreatments([]);
    setPreviewCart([]);
    setPreviewPurchases([]);
    setPreviewError('');
    setPreviewNotice('');
    setPreviewSkinSessionId('');
    setPreviewHairSessionId('');
    setPreviewLoading(true);
    const fallbackSessions = snapshotReportSessions(checkin);
    try {
      await setCheckinProductPreview(checkin.id, true);
      try {
        const sessions = await fetchCheckinReportSessions(checkin.id);
        const apiHair = String(sessions.hair_insight_session_id || '').trim();
        const apiSkin = String(sessions.skin_session_id || '').trim();
        setPreviewSkinSessionId(
          apiSkin && !apiSkin.toUpperCase().startsWith('HA-')
            ? apiSkin
            : fallbackSessions.skinSessionId || '',
        );
        setPreviewHairSessionId(
          apiHair.startsWith('HA-') ? apiHair : fallbackSessions.hairSessionId || '',
        );
      } catch {
        setPreviewSkinSessionId(fallbackSessions.skinSessionId);
        setPreviewHairSessionId(fallbackSessions.hairSessionId);
      }
      const data = await fetchCheckinRecommendedProducts(checkin.id);
      setPreviewProducts(data?.recommended_products || { morning: [], evening: [] });
      setPreviewTreatments(
        Array.isArray(data?.recommended_treatments) ? data.recommended_treatments : [],
      );
      setPreviewCart(Array.isArray(data?.product_cart) ? data.product_cart : []);
      setPreviewPurchases(Array.isArray(data?.product_purchases) ? data.product_purchases : []);
      const snap = checkin?.customer_snapshot || {};
      if (!data?.product_cart?.length && Array.isArray(snap.product_cart)) {
        setPreviewCart(snap.product_cart);
      }
      if (!data?.product_purchases?.length && Array.isArray(snap.product_purchases)) {
        setPreviewPurchases(snap.product_purchases);
      }
    } catch (err) {
      setPreviewError(err?.message || 'Could not load recommendations');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const closeFinishedPreview = useCallback(async () => {
    const id = previewCheckin?.id;
    setPreviewCheckin(null);
    setPreviewProducts(null);
    setPreviewTreatments([]);
    setPreviewCart([]);
    setPreviewPurchases([]);
    setPreviewError('');
    setPreviewNotice('');
    setPreviewSkinSessionId('');
    setPreviewHairSessionId('');
    if (!id) return;
    try {
      await setCheckinProductPreview(id, false);
    } catch {
      /* non-blocking */
    }
  }, [previewCheckin?.id]);

  // Keep desk cart in sync with Passport "Send to front desk" while preview is open.
  useEffect(() => {
    const checkinId = previewCheckin?.id;
    if (!checkinId) return undefined;
    let cancelled = false;
    const syncCart = async () => {
      if (cancelled || previewBusy) return;
      try {
        const data = await fetchCheckinRecommendedProducts(checkinId);
        if (cancelled) return;
        if (Array.isArray(data?.product_cart)) setPreviewCart(data.product_cart);
        if (Array.isArray(data?.product_purchases)) setPreviewPurchases(data.product_purchases);
      } catch {
        /* ignore poll errors */
      }
    };
    const timer = setInterval(syncCart, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [previewCheckin?.id, previewBusy]);

  const handleSavePreviewCart = useCallback(
    async (items) => {
      if (!previewCheckin?.id) return;
      setPreviewBusy(true);
      try {
        const data = await saveCheckinProductCart(previewCheckin.id, items, { source: 'lobby' });
        setPreviewCart(Array.isArray(data?.product_cart) ? data.product_cart : items);
        setPreviewNotice('Cart saved for front desk.');
        try {
          window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
        } catch {
          /* ignore */
        }
      } finally {
        setPreviewBusy(false);
      }
    },
    [previewCheckin?.id],
  );

  const handlePlacePreviewOrder = useCallback(
    async (items) => {
      if (!previewCheckin?.id) return;
      setPreviewBusy(true);
      try {
        const data = await placeCheckinProductOrder(previewCheckin.id, items, { source: 'lobby' });
        setPreviewCart([]);
        setPreviewPurchases(Array.isArray(data?.product_orders) ? data.product_orders : []);
        setPreviewNotice(`Purchase saved · ${data?.order_id || 'CHK order'}`);
        try {
          window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
        } catch {
          /* ignore */
        }
      } finally {
        setPreviewBusy(false);
      }
    },
    [previewCheckin?.id],
  );

  return (
    <div className="fi-lux-theme fi-scroll-page salon-visit-intent salon-service-lobby-page page-bg salon-hub-has-inline-logout">
      <header className="salon-flow-select-header salon-service-lobby__nav-header">
        <button
          type="button"
          className="salon-flow-select-icon-btn salon-flow-select-header-back"
          onClick={handleBack}
          aria-label="Go back"
          title="Back"
        >
          <BackIcon />
        </button>
        <div className="salon-flow-select-header-actions">
          <button
            type="button"
            className="salon-flow-select-icon-btn"
            onClick={() => refresh()}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshIcon />
          </button>
          <SalonHeaderActions
            homeButtonClassName="salon-flow-select-icon-btn"
            logoutButtonClassName="salon-flow-select-icon-btn"
            showHome={false}
            onHome={handleHome}
            onLogout={handleLogout}
            logoutBusy={logoutBusy}
          />
        </div>
      </header>
      <div className="salon-visit-intent__content">
        <header className="salon-visit-intent__brand salon-visit-intent__brand--logo-only">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
          />
        </header>

        <h1 className="salon-visit-intent__title">Guest list</h1>
        <p className="salon-visit-intent__lede">
          Stylist-led visits appear here when service starts. Front desk sees the same activity on Live floor.
        </p>

        {newGuestAlert ? (
          <p className="salon-service-session__banner" role="status">
            New guest checked in.
            <button
              type="button"
              className="salon-service-session__link"
              style={{ marginLeft: '0.5rem' }}
              onClick={() => setNewGuestAlert(false)}
            >
              Dismiss
            </button>
          </p>
        ) : null}

        {error ? <p className="salon-service-session__error">{error}</p> : null}

        <div className="salon-service-lobby__panel">
          {loading && !rows.length && !finishedRows.length ? (
            <p className="salon-visit-intent__lede salon-service-lobby__panel-lede">Loading…</p>
          ) : null}

          <section className="salon-service-lobby__continue">
            <h2 className="salon-service-lobby__continue-title">
              Active now
              {rows.length ? (
                <span className="salon-service-lobby__count">{rows.length}</span>
              ) : null}
            </h2>
            {!loading && !rows.length ? (
              <p className="salon-visit-intent__lede salon-service-lobby__panel-lede">No guests in queue.</p>
            ) : null}
            {rows.length ? (
              <ul className="salon-service-lobby__list">
                {rows.map(({ checkin, visitPins }) => {
                  const action = lobbyActionForVisit(checkin, visitPins);
                  const intent = readVisitIntentOrNull(checkin);
                  const live = deskServiceShortLabel(checkin.selected_services);
                  const queued = pendingAnalysisLines(checkin.selected_services)
                    .map((l) => l.name)
                    .filter(Boolean)
                    .join(', ');
                  const lobbyPreview = lobbyShowAnalysisPreview(checkin);
                  const lobbyReel = pickLobbyReel(reelsByCheckin, checkin.id);
                  const sub =
                    live ||
                    queued ||
                    (lobbyPreview ? 'Analysis complete · products ready' : '') ||
                    (intent ? visitIntentLabel(intent) : '') ||
                    (isSalonReadyForStylist(checkin) ? 'Ready — pick stylist' : 'Waiting for front desk');
                  return (
                    <li key={checkin.id} className="salon-service-lobby__finished-item">
                      <button
                        type="button"
                        className="salon-service-lobby__continue-row"
                        disabled={action.mode === 'disabled'}
                        onClick={() => openGuest(checkin, action.pin, action.mode)}
                      >
                        <div>
                          <strong>
                            {checkin.customer_name || 'Guest'}
                            {checkin.customer_code ? ` · ${checkin.customer_code}` : ''}
                          </strong>
                          <span>{sub}</span>
                        </div>
                        <span className="salon-service-lobby__continue-cta">{action.label} →</span>
                      </button>
                      <div className="salon-service-lobby__row-actions">
                        {visitCanContinueFromLobby(checkin) ? (
                          <button
                            type="button"
                            className="salon-service-lobby__preview-btn"
                            onClick={() => continueGuestVisit(checkin)}
                          >
                            Analysis & services
                          </button>
                        ) : null}
                        {lobbyPreview ? (
                          <button
                            type="button"
                            className="salon-service-lobby__preview-btn"
                            onClick={() => openFinishedPreview(checkin)}
                          >
                            {lobbyPreviewCtaLabel(checkin)}
                          </button>
                        ) : null}
                        {lobbyReel?.has_video ? (
                          <button
                            type="button"
                            className="salon-service-lobby__preview-btn"
                            disabled={reelBusyId === lobbyReel.id}
                            onClick={() => handleReelDownload(lobbyReel)}
                          >
                            {reelBusyId === lobbyReel.id ? 'Downloading…' : 'Download reel'}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>

          <section className="salon-service-lobby__continue salon-service-lobby__finished">
            <h2 className="salon-service-lobby__continue-title">
              Finished today
              {finishedRows.length ? (
                <span className="salon-service-lobby__count">{finishedRows.length}</span>
              ) : null}
            </h2>
            <p className="salon-service-lobby__finished-hint">
              Guests appear here after a service is started and completed. Analysis report and products only when skin or hair analysis ran.
            </p>
            {!loading && !finishedRows.length ? (
              <p className="salon-visit-intent__lede salon-service-lobby__panel-lede">No finished guests yet.</p>
            ) : null}
            {finishedRows.length ? (
              <ul className="salon-service-lobby__list">
                {finishedRows.map((checkin) => {
                  const when = formatFinishedTime(checkin);
                  const svc =
                    deskServiceShortLabel(checkin.selected_services) || 'Service completed';
                  const snap = checkin.customer_snapshot || {};
                  const cartPending = Boolean(snap.product_cart_pending) ||
                    (Array.isArray(snap.product_cart) && snap.product_cart.length > 0);
                  const purchased = Array.isArray(snap.product_purchases) && snap.product_purchases.length > 0;
                  const lobbyReel = pickLobbyReel(reelsByCheckin, checkin.id);
                  const showAnalysisPreview = lobbyShowAnalysisPreview(checkin);
                  return (
                    <li key={`done-${checkin.id}`} className="salon-service-lobby__finished-item">
                      <div className="salon-service-lobby__finished-info">
                        <strong>
                          {checkin.customer_name || 'Guest'}
                          {checkin.customer_code ? ` · ${checkin.customer_code}` : ''}
                        </strong>
                        <span>
                          {svc}
                          {when ? ` · ${when}` : ''}
                          {cartPending ? ' · Cart pending' : ''}
                          {purchased ? ' · Purchased' : ''}
                          {!cartPending && hasAnalysisProductsReady(checkin) ? ' · Products ready' : ''}
                        </span>
                      </div>
                      <div className="salon-service-lobby__row-actions">
                        {visitCanContinueFromLobby(checkin) ? (
                          <button
                            type="button"
                            className="salon-service-lobby__preview-btn"
                            onClick={() => continueGuestVisit(checkin)}
                          >
                            Open visit
                          </button>
                        ) : null}
                        {showAnalysisPreview ? (
                          <button
                            type="button"
                            className="salon-service-lobby__preview-btn"
                            onClick={() => openFinishedPreview(checkin)}
                          >
                            {cartPending ? 'Review cart' : lobbyPreviewCtaLabel(checkin)}
                          </button>
                        ) : null}
                        {lobbyReel?.has_video ? (
                          <button
                            type="button"
                            className="salon-service-lobby__preview-btn"
                            disabled={reelBusyId === lobbyReel.id}
                            onClick={() => handleReelDownload(lobbyReel)}
                          >
                            {reelBusyId === lobbyReel.id ? 'Downloading…' : 'Download reel'}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        </div>

        <ExpectedTodayBookings className="salon-service-lobby__expected" />
        <div className="salon-service-lobby__guest-checkin">
          <button
            type="button"
            className="salon-service-lobby__guest-checkin-btn"
            onClick={() => navigate(SALON_SERVICE_CATALOG_PATH)}
          >
            Service menu
          </button>
          <button
            type="button"
            className="salon-service-lobby__guest-checkin-btn"
            onClick={openGuestCheckInScreen}
          >
            Check in customer
          </button>
          <p className="salon-service-lobby__guest-checkin-hint">
            Bookings open stylist check-in to confirm or register the guest (gender required).
          </p>
        </div>
      </div>

      <RecommendedProductsPreviewModal
        open={Boolean(previewCheckin)}
        onClose={closeFinishedPreview}
        customerName={previewCheckin?.customer_name || 'Guest'}
        products={previewProducts}
        treatments={previewTreatments}
        loading={previewLoading}
        busy={previewBusy}
        error={previewError}
        notice={previewNotice}
        initialCart={previewCart}
        purchases={previewPurchases}
        allowCart
        allowPlaceOrder
        onSaveCart={handleSavePreviewCart}
        onPlaceOrder={handlePlacePreviewOrder}
        subtitle="Scan the report QR, then review recommended services and products."
        skinSessionId={previewSkinSessionId}
        hairSessionId={previewHairSessionId}
      />
    </div>
  );
}
